import {
  useIsFetching,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import type { InferTransformedProcedureOutput } from "@goose/api";
import type { Requirement } from "@goose/schema/Requirements";
import { filterMap, None, Some } from "@goose/utils/option";

import { AIDisclosure } from "~/components/AIDisclosure";
import { CollapsibleText } from "~/components/CollapsibleText";
import { ContactDetails } from "~/components/ContactDetails";
import { ContactEvaluationChat } from "~/components/ContactEvaluationChat";
import { ErrorDisplay } from "~/components/ErrorDisplay";
import { EvaluationDetails } from "~/components/EvaluationDetails";
import { EvaluationSummary } from "~/components/EvaluationSummary";
import { OpportunityDetails } from "~/components/OpportunityDetails";
import { PendingContent } from "~/components/PendingContent";
import { SignOut } from "~/components/SignOut";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { Spinner } from "~/components/ui/Spinner";
import { ContactIndex } from "~/lib/search/contact";
import { OpportunityIndex } from "~/lib/search/opportunity";
import { SearchIndexContext } from "~/lib/search/search-index";
import { renderTitle } from "~/lib/title";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute(
  "/_authenticated/opportunity/$opportunityId/contact/$contactId/",
)({
  component: ContactPage,
});

type ExtractResults =
  InferTransformedProcedureOutput<"opportunities.requirements.extract">;

type RequirementSet = ExtractResults["requirementSet"];
type Opportunity = ExtractResults["opportunity"];

function importance({ relevance, scarcity }: Requirement) {
  return relevance * scarcity;
}

interface EvaluationProps {
  contactId: string;
  opportunity: Opportunity;
  requirementSet: RequirementSet;
}

function Evaluation({
  contactId,
  opportunity,
  requirementSet,
}: EvaluationProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const [updatedRequirementSet, setUpdatedRequirementSet] =
    useState<InferTransformedProcedureOutput<"opportunities.requirements.modify"> | null>(
      null,
    );

  const [updatedEvaluation, setUpdatedEvaluation] =
    useState<InferTransformedProcedureOutput<"contacts.evaluations.clarify"> | null>(
      null,
    );

  const query = useQuery(
    trpc.contacts.evaluations.ensure.queryOptions({
      requirementSetId: requirementSet.id,
      contactId,
    }),
  );

  const modifyRequirementsMutation = useMutation(
    trpc.opportunities.requirements.modify.mutationOptions(),
  );

  const clarifyFitMutation = useMutation(
    trpc.contacts.evaluations.clarify.mutationOptions(),
  );

  const currentRequirementSet = updatedRequirementSet ?? requirementSet;
  const currentEvaluation = updatedEvaluation ?? query.data?.evaluation;

  const evaluationPending = useIsFetching({
    queryKey: trpc.contacts.evaluations.ensure.queryKey({
      requirementSetId: modifyRequirementsMutation.data?.id,
      contactId,
    }),
  });

  const isPending =
    evaluationPending !== 0 ||
    modifyRequirementsMutation.isPending ||
    clarifyFitMutation.isPending;

  const modifyRequirements = async (modification: string) => {
    const requirementSet = await modifyRequirementsMutation.mutateAsync({
      modification,
      requirementSetId: currentRequirementSet.id,
    });

    const { evaluation } = await queryClient.fetchQuery(
      trpc.contacts.evaluations.ensure.queryOptions({
        requirementSetId: requirementSet.id,
        contactId,
      }),
    );

    setUpdatedRequirementSet(requirementSet);
    setUpdatedEvaluation(evaluation);

    return {
      requirements: requirementSet.requirements,
      results: evaluation.results,
    };
  };

  const clarifyFit = async (clarification: string) => {
    if (!currentEvaluation) {
      throw new Error("No current evaluation");
    }

    const evaluation = await clarifyFitMutation.mutateAsync({
      clarification,
      evaluationId: currentEvaluation.id,
    });

    setUpdatedEvaluation(evaluation);
    return evaluation.results;
  };

  const results = useMemo(() => {
    if (!currentEvaluation) {
      return [];
    }

    const requirementsById = new Map(
      currentRequirementSet.requirements.map((requirement) => [
        requirement.id,
        requirement,
      ]),
    );

    return filterMap(currentEvaluation.results, (fit) => {
      const requirement = requirementsById.get(fit.requirementId);
      if (!requirement) {
        return None;
      }
      return Some({ fit, requirement });
    }).sort((a, b) => importance(b.requirement) - importance(a.requirement));
  }, [currentEvaluation, currentRequirementSet]);

  const searchIndexes = useMemo(() => {
    if (!query.data) return {};
    return {
      contact: new ContactIndex(query.data.contact),
      opportunity: new OpportunityIndex(opportunity),
    };
  }, [query.data, opportunity]);

  if (query.isLoading) {
    return (
      <div className="mt-6 flex items-center justify-center gap-3 text-gray-600">
        <Spinner size="md" />
        <span>Evaluating fit…</span>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="mt-6">
        <TRPCClientErrorDisplay error={query.error} />
      </div>
    );
  }

  if (!query.data || !currentEvaluation) {
    return (
      <div className="mt-6">
        <ErrorDisplay message="No evaluation found" level="warning" />
      </div>
    );
  }

  const clarificationResults = results.filter(
    (result) => result.fit.needsClarification,
  );

  return (
    <SearchIndexContext.Provider value={searchIndexes}>
      <title>
        {renderTitle(
          `${query.data.contact.name} (${query.data.contact.shortId})`,
          opportunity.title
            ? `${opportunity.title} (${opportunity.shortId})`
            : opportunity.shortId,
        )}
      </title>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Requirements */}
        <div className="space-y-4 lg:col-span-2">
          <EvaluationSummary
            evaluation={currentEvaluation}
            results={results}
            isPending={isPending}
            opportunityId={opportunity.id}
          />

          {clarificationResults.length > 0 && (
            <PendingContent
              className="bg-white shadow-sm ring-1 ring-amber-200 rounded-lg p-5"
              isPending={isPending}
            >
              <h3 className="text-lg font-semibold text-amber-800">
                Needs clarification ({clarificationResults.length})
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                These items may require follow-up with the candidate.
              </p>
              <div className="mt-3 space-y-3">
                {clarificationResults.map(({ fit, requirement }) => (
                  <div
                    key={fit.id}
                    className="rounded-md border border-amber-200 bg-amber-50 p-3"
                  >
                    <div className="font-medium text-amber-900">
                      {requirement.name}
                    </div>
                    {fit.needsClarification && (
                      <CollapsibleText
                        className="mt-1 text-sm"
                        text={fit.needsClarification}
                        preserveWhitespace
                        previewChars={180}
                      />
                    )}
                  </div>
                ))}
              </div>
            </PendingContent>
          )}

          <EvaluationDetails results={results} isPending={isPending} />
        </div>

        {/* Right column: Details */}
        <div className="space-y-4">
          <ContactDetails contact={query.data.contact} />
          <OpportunityDetails opportunity={opportunity} />
        </div>
      </div>

      <ContactEvaluationChat
        evaluationId={currentEvaluation.id}
        unread={clarificationResults.length}
        onClarifyFit={clarifyFit}
        onModifyRequirements={modifyRequirements}
      />
    </SearchIndexContext.Provider>
  );
}

function ContactPage() {
  const trpc = useTRPC();
  const { opportunityId, contactId } = Route.useParams();

  const query = useQuery(
    trpc.opportunities.requirements.extract.queryOptions({ id: opportunityId }),
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Condensed Header */}
        <div className="flex items-center gap-3 mb-4">
          <img
            src="/web-app-manifest-512x512.png"
            alt="Goose"
            width={28}
            height={28}
            className="inline-block"
          />
          <div>
            <div className="text-xl font-semibold text-gray-900">Goose</div>
            <div className="text-xs text-gray-600">
              The Account Manager&apos;s AI Wingman
            </div>
          </div>
        </div>

        <AIDisclosure />

        {query.isLoading && (
          <div className="mt-6 flex items-center justify-center gap-3 text-gray-600">
            <Spinner size="md" />
            <span>Extracting requirements…</span>
          </div>
        )}

        {query.error && (
          <div className="mt-6">
            <TRPCClientErrorDisplay error={query.error} />
          </div>
        )}

        {query.data && (
          <Evaluation
            key={query.data.requirementSet.id}
            contactId={contactId}
            opportunity={query.data.opportunity}
            requirementSet={query.data.requirementSet}
          />
        )}
      </div>
      <div className="mt-2 text-center">
        <SignOut />
      </div>
    </div>
  );
}
